import { filterLoyaltyProviders } from "../helpers/commonHelpers";
import { isPointsLineAsV1 } from "../promotions/entry";
import { providerPublishesProgram } from "./provider";
import type {
  LoyaltyDetails,
  LoyaltyPointsDiscount,
  LoyaltyProvider,
  ProgramDetailsEnvelope,
} from "./types";
import { selectOrderingBranchId } from "../orderingContext/selectors";

/**
 * 🔴 **The ONLY file in `packages/core/loyalty` that names a redux key.** Every rule module takes
 * its inputs as arguments and every hook reads through these, so a store reshape is a change here
 * and nowhere else — and the rules stay testable without a store.
 *
 * Plain selectors taking the whole state, so the same function serves `useSelector` in a
 * component and `store.getState()` in the adapter. Nothing is read at module-eval time (audit
 * checks H/J).
 */

/**
 * The channel the loyalty flow works in: the BILL's own `channelDefId`, else the selected
 * channel's while the bill has none yet.
 *
 * It keys the provider lookup AND `programDetailsMap`, which is written and read back under it.
 * `useEnsureProgramDetails` fetches with it and `selectLoyaltyProgramDetails` looks up with it.
 * Two sources for "the current channel" would cache under one and miss under the other,
 * permanently and silently.
 *
 * 🔴 **The bill comes first (2026-09-24).** `selectedChannelData` is New Order's channel picker,
 * not the bill's channel. A checkout opened on a saved bill writes the bill into `saleData` and
 * leaves the picker alone. Mobile's tab bar goes further and resets the picker to the merchant's
 * default take-new-order channel whenever you move between New Order and My Orders
 * (`routes/BottomTabs` `handleTabSwitch`). So a bill taken on any other channel was checked out
 * against the default channel's provider and programme. For DotPe that meant no points panel
 * whenever the default channel had no live POS-burning programme, and the default channel's rules
 * when it had one. A provider scoped to the bill's channel was not found at all. On a new sale the two agree, because every channel switch writes
 * both (`updateAndSaveChannel`), and a blank sale has `channelDefId: ''`, so it falls through.
 */
export const selectLoyaltyChannelDefId = (state: any): string | undefined =>
  state.saleDataStore?.saleData?.channelDefId ||
  state.saleDataStore?.selectedChannelData?.channelDefId;

/**
 * The active loyalty provider for the current branch + the bill's channel, or `null`.
 *
 * Prefers the provider flagged `isPrimary`, else the first match.
 *
 * ⚠️ Moved here from `redux-store/selectors/loyaltySelectors` on 2026-09-04 so it could live
 * beside the rules that consume it; that file re-exports it, so its importers are unchanged.
 * One definition.
 */
export const selectActiveLoyaltyProvider = (state: any): LoyaltyProvider | null => {
  const loyaltyProviders: any[] = state.businessStore?.businessData?.loyaltyProviders;
  // the order's branch (NEW_ORDER_V2_PLAN.md §5): the session's, unless a chosen-branch flow set one
  const branchId: string = selectOrderingBranchId(state) ?? "";
  const channelDefId: string = selectLoyaltyChannelDefId(state) ?? "";
  const params: Record<string, string> = { branchId, channelDefId };
  const providers = filterLoyaltyProviders(loyaltyProviders ?? [], params);
  if (providers?.length > 0) {
    const primaryProvider = providers.find((p: any) => p.isPrimary);
    return primaryProvider ?? providers[0];
  }
  return null;
};

/** Every configured provider, unfiltered. Needed to resolve a discount back to its owner. */
export const selectLoyaltyProviders = (state: any): LoyaltyProvider[] =>
  state.businessStore?.businessData?.loyaltyProviders ?? [];

/**
 * The loyalty programme for the BILL'S CHANNEL (`selectLoyaltyChannelDefId`) — or `undefined`
 * unless the ACTIVE provider publishes programmes.
 *
 * 🔴 **Paired with the provider HERE, so every store read is** (2026-09-17). `programDetailsMap`
 * is keyed by channel and a cached entry outlives a provider change; unpaired, a partner with no
 * programme was shown another partner's minimum order value, cap sentence, collectible name and
 * "Know more" rules. Pure rules handed an envelope as an ARGUMENT still pair it themselves
 * (`canRedeemPointsOnPos`, `skipsEnrollmentOtp`, `getRedemptionPanelState`, …) — a caller can pass
 * them anything.
 *
 * ponytail: pairs by CAPABILITY, not by provider id. That is exact while DotPe is the only partner
 * with a programme — `program/fetch` takes no provider and answers for DotPe alone, and
 * `useProgramDetailsFetch` only asks under it. If a second partner ever publishes one, key the
 * cache by provider instead.
 */
export const selectLoyaltyProgramDetails = (state: any): ProgramDetailsEnvelope | undefined => {
  if (!providerPublishesProgram(selectActiveLoyaltyProvider(state)?.metaId)) return undefined;
  const programDetailsMap = state.LoyaltyStore?.programDetailsMap;
  const channelDefId = selectLoyaltyChannelDefId(state);
  return channelDefId ? programDetailsMap?.[channelDefId] : undefined;
};

/**
 * The loyalty-points discount on the current bill, or `undefined`.
 *
 * 🔴 Identified ONLY by `isPointsLineAsV1` (today's rule, kept for the live module until cutover:
 * DISCOUNT_V2_PLAN.md decision 59). Never re-derive that test —
 * `docs/flows/LOYALTY_REFUND.md` records what a second inline copy cost in production.
 */
export const selectLoyaltyDiscount = (state: any): LoyaltyPointsDiscount | undefined =>
  state.saleDataStore?.saleData?.billDiscountList?.find((d: any) => isPointsLineAsV1(d));

/** The cached balance for one provider, or `undefined` when it has not been fetched yet. */
export const selectProviderLoyaltyDetails = (
  state: any,
  providerMid?: string,
): LoyaltyDetails | undefined =>
  providerMid ? state.LoyaltyStore?.loyaltyDetails?.[providerMid] : undefined;

/** The balance for whichever provider is active. `undefined` while loading. */
export const selectActiveProviderLoyaltyDetails = (state: any): LoyaltyDetails | undefined =>
  selectProviderLoyaltyDetails(state, selectActiveLoyaltyProvider(state)?.mid);

/**
 * The provider the redemption APIs will act on.
 *
 * ⚠️ NOT the same as `selectActiveLoyaltyProvider`: this is what the panel last wrote into the
 * slice, and it is what `redeemPoints` / `verifyRedeemOtp` build their payloads from. The two
 * agree because the panel keeps them in step — but the APIs follow this one.
 */
export const selectSelectedLoyaltyProvider = (state: any): LoyaltyProvider | undefined =>
  state.LoyaltyStore?.selectedLoyaltyProvider;

export const selectSaleData = (state: any): any => state.saleDataStore?.saleData;

export const selectIsOffline = (state: any): boolean => !!state.businessStore?.isOffline;

export const selectBusinessData = (state: any): any => state.businessStore?.businessData;

/**
 * The customer's phone, from the customer store or falling back to the sale.
 *
 * ⚠️ The two spell it differently — `contactInfo.phone` on the store record,
 * `contactInfo.phoneNumber` on the sale. Both spellings are load-bearing; this is the one place
 * that has to know.
 */
export const selectCustomerPhone = (state: any): string | undefined =>
  state.customerStore?.customerInfo?.contactInfo?.phone ||
  state.saleDataStore?.saleData?.customerInfo?.contactInfo?.phoneNumber;

/**
 * Single source of truth for the current customer's loyalty info, populated from every customer
 * fetch path. `null` when no customer is loaded or they have none.
 */
export const selectCustomerLoyaltyInfo = (state: any): any =>
  state.customerStore?.externalLoyaltyData?.loyaltyInfo ?? null;

/**
 * WHICH customer the cached `loyaltyInfo` belongs to, or `undefined` when unknown.
 *
 * 🔴 **Presence of `loyaltyInfo` does not mean it is THIS customer's.** `externalLoyaltyData`
 * survives a customer swap, so "already loaded" has to be asked as an identity question — see
 * `ensureCustomerLoyaltyInfo`. The saga stamps this only on the internal-ref fetch path; an
 * external (phone-keyed) fetch leaves it `undefined`, which correctly reads as "not known to be
 * this customer's".
 */
export const selectCustomerLoyaltyRefId = (state: any): string | undefined =>
  state.customerStore?.externalLoyaltyData?.customerRefId;

/** Single source of truth for the current customer's provider consents. `null` when unknown. */
export const selectCustomerProviderConsents = (state: any): Record<string, boolean> | null =>
  state.customerStore?.externalLoyaltyData?.providerConsents ?? null;

/**
 * A customer who is absent from our DB but exists at the provider, for prefilling the
 * new-customer form. `null` whenever the customer IS in our DB — `customerInfo` stays empty in
 * this case so the new-customer flow is preserved.
 */
export const selectExternalCustomer = (state: any): any =>
  state.customerStore?.externalLoyaltyData?.customer ?? null;

/**
 * A stable key for "which customer is loaded" — `mid` when the detail fetch has populated it,
 * else `customerId`.
 *
 * Consent recorded during one customer's visit must not carry into the next, and this is what
 * that reset watches. `mid` is the more stable identifier; `customerId` covers searches and
 * edit-mode hydration where `mid` is not set yet.
 */
export const selectCustomerRecordKey = (state: any): string | undefined =>
  state.customerStore?.customerInfo?.mid || state.customerStore?.customerInfo?.customerId;
